/**
 * GDSApiService.js
 * Handles all network requests for the GDS Builder.
 */

import { getSizedShopifyUrl, MODAL_IMAGE_PREVIEW_WIDTH } from './GDSModules.js';

/**
 * A custom error class for API requests to provide more context.
 * @param {string} message - A human-readable error message.
 * @param {number} status - The HTTP status code of the response.
 * @param {Object} body - The parsed JSON error body.
 */
export class ApiError extends Error {
    constructor(message, status, body) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.body = body;
    }
}

export class GDSApiService {
    constructor(apiEndpoint = '/apps/gds', targetEndpoint = 'gds-builder-v1') {
        this.apiEndpoint = apiEndpoint;
        this.targetEndpoint = targetEndpoint;
        this.productCache = new Map(); // Cache for product data
        this.preloadedImages = new Set();
    }

    /**
     * Private helper to consolidate all POST requests to the backend proxy.
     * @private
     */
    async _postToProxy(targetEndpoint, body = {}) {
        const headers = new Headers({
            'Content-Type': 'application/json',
            'X-Target-Endpoint': targetEndpoint
        });

        const response = await fetch(this.apiEndpoint, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            const errorBody = await response.json().catch(() => ({
                error: "Request failed with a non-JSON response.",
                details: `Status ${response.status}: ${response.statusText}`
            }));

            // Add validation error detection
            if (response.status === 400 || response.status === 422) {
                const validationError = new ApiError(
                    errorBody.details || errorBody.error || 'Invalid data provided',
                    response.status,
                    errorBody
                );
                validationError.isValidationError = true;
                throw validationError;
            }

            throw new ApiError(errorBody.details || errorBody.error, response.status, errorBody);
        }

        return response.json();
    }

    /**
     * Fetches builder data from the main backend endpoint.
     */
    async fetchData(data) {
        return this._postToProxy(this.targetEndpoint, data);
    }

    /**
     * Creates a configuration snapshot via the API.
     */
    async createSnapshot(configuration) {
        return this._postToProxy('gds-builder-configuration-snapshots-v1', configuration);
    }

    /**
     * Loads a configuration from a snapshot ID.
     */
    async loadSnapshot(snapshotId) {
        return this.fetchData({ load_from_snapshot_id: snapshotId });
    }

    /**
     * Fetches product data using the public .js endpoint, with caching.
     */
    async fetchProductData(handle) {
        if (this.productCache.has(handle)) {
            return this.productCache.get(handle);
        }

        try {
            const response = await fetch(`${window.Shopify.routes.root}products/${handle}.js`);
            if (!response.ok) {
                console.error(`Failed to fetch product ${handle}: ${response.status}`);
                return null;
            }
            const productData = await response.json();
            this.productCache.set(handle, productData); // Store successful response in cache
            return productData;
        } catch (error) {
            console.error(`Error fetching product ${handle}:`, error);
            return null;
        }
    }

    /**
     * Fetches variant metafields from the backend proxy.
     */
    async fetchVariantMetafields(variantId) {
        const body = {
            variant_id: `gid://shopify/ProductVariant/${variantId}`
        };
        return this._postToProxy('variant-metafields-v1', body);
    }

    /**
     * Adds selected items to the Shopify cart.
     */
    async addToCart(items) {
        const data = {
            items,
            sections: ['minicart-items', 'cart-toggle-with-item-count'],
        };

        const response = await fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            const errorBody = await response.json().catch(() => ({ error: "Cart operation failed" }));
            throw new ApiError(errorBody.description || errorBody.message || 'Failed to add items to cart.', response.status, errorBody);
        }

        return response.json();
    }

    /**
     * Fetches product data and triggers a browser preload for its featured image.
     * @param {string} handle - The product handle.
     */
    async preloadProductImage(handle) {
        if (!handle || this.preloadedImages.has(handle)) {
            return; // Don't run if no handle or already attempted
        }

        // Mark as attempted immediately to prevent duplicate requests
        this.preloadedImages.add(handle);

        try {
            // Use the existing cached fetch method
            const productData = await this.fetchProductData(handle);

            if (productData && productData.featured_image) {
                // Use the imported utility function and constant
                const sizedImageUrl = getSizedShopifyUrl(
                    productData.featured_image,
                    MODAL_IMAGE_PREVIEW_WIDTH, // Use constant for width
                    MODAL_IMAGE_PREVIEW_WIDTH  // Assuming square aspect ratio for modal preview
                );

                if (sizedImageUrl) {
                    const img = new Image();
                    img.src = sizedImageUrl; // This starts the browser download
                }
            }
        } catch (error) {
            console.error(`Error during image preload for handle ${handle}:`, error);
        }
    }
}
